class PredictiveSearch extends HTMLElement {
  connectedCallback () {
    if (this.isInitialized) return

    this.form = this.querySelector('form.search-form')
    this.input = this.form?.querySelector('input[type="search"]')
    this.resetButton = this.form?.querySelector('button[type="reset"]')
    this.submitButton = this.form?.querySelector('button[type="submit"]')
    this.loadingIndicator = this.form?.querySelector('.loading-spinner-wrapper')
    this.resultsContainer = this.querySelector('#predictive-search-results')
    this.status = this.querySelector('#predictive-search-status')

    if (!this.form || !this.input || !this.resetButton || !this.submitButton || !this.loadingIndicator || !this.resultsContainer || !this.status) return

    this.currentIndex = -1
    this.listenerController = new AbortController()
    const signal = this.listenerController.signal

    this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), 300), { signal })
    this.input.addEventListener('focus', this.handleFocus.bind(this), { signal })
    this.addEventListener('keydown', this.handleKeyDown.bind(this), { signal })
    this.resetButton.addEventListener('click', this.handleReset.bind(this), { signal })
    this.resultsContainer.addEventListener('click', this.handleResultsClick.bind(this), { signal })

    const dialog = this.closest('dialog')

    if (dialog) {
      this.dialogObserver = new MutationObserver(() => {
        if (dialog.open) window.setTimeout(() => this.input.focus(), 100)
      })
      this.dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] })
    }

    this.isInitialized = true
  }

  disconnectedCallback () {
    this.listenerController?.abort()
    this.dialogObserver?.disconnect()
    this.requestController?.abort()
    window.clearTimeout(this.debounceTimer)
    this.isInitialized = false
  }

  handleFocus () {
    if (this.input.value.trim() && this.resultsContainer.innerHTML.trim()) {
      this.resultsContainer.removeAttribute('hidden')
      this.input.setAttribute('aria-expanded', 'true')
    }
  }

  handleInput (event) {
    const query = event.target.value.trim()

    if (query) {
      this.resetButton.removeAttribute('hidden')
      this.fetchSuggestions(query)
      return
    }

    this.resetButton.setAttribute('hidden', 'hidden')
    this.clearSuggestions()
  }

  handleReset () {
    window.setTimeout(() => {
      this.input.focus()
      this.input.dispatchEvent(new Event('input'))
    })
  }

  handleResultsClick (event) {
    if (event.target.closest('#predictive-search-search-for-button')) this.form.requestSubmit()
  }

  async fetchSuggestions (query) {
    this.requestController?.abort()
    const requestController = new AbortController()
    this.requestController = requestController
    const url = `${this.dataset.route}?q=${encodeURIComponent(query)}&resources[type]=${this.dataset.resourceTypes}&resources[limit]=${this.dataset.limit}&section_id=predictive-search`

    try {
      this.setLoading(true)
      const response = await fetch(url, { signal: requestController.signal })

      if (!response.ok) throw new Error(response.statusText)

      this.renderSuggestions(await response.text())
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.clearSuggestions()
        console.error('Error fetching predictive search suggestions:', error)
      }
    } finally {
      if (this.requestController === requestController && !requestController.signal.aborted) this.setLoading(false)
    }
  }

  renderSuggestions (text) {
    const section = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector('#shopify-section-predictive-search')

    if (!section) {
      this.clearSuggestions()
      return
    }

    this.resultsContainer.innerHTML = section.innerHTML
    this.resultsContainer.removeAttribute('hidden')
    this.input.setAttribute('aria-expanded', 'true')
    this.currentIndex = -1

    const count = this.resultsContainer.querySelector('[data-predictive-search-count]')
    if (count) this.status.textContent = count.textContent.trim()
  }

  clearSuggestions () {
    this.requestController?.abort()
    this.resultsContainer.innerHTML = ''
    this.resultsContainer.setAttribute('hidden', 'hidden')
    this.resultsContainer.removeAttribute('aria-busy')
    this.input.setAttribute('aria-expanded', 'false')
    this.input.removeAttribute('aria-activedescendant')
    this.status.textContent = ''
    this.currentIndex = -1
    this.setLoading(false)
  }

  setLoading (isLoading) {
    this.loadingIndicator.toggleAttribute('hidden', !isLoading)
    this.submitButton.toggleAttribute('hidden', isLoading)

    if (isLoading) {
      this.resultsContainer.setAttribute('aria-busy', 'true')
      this.status.textContent = this.dataset.textLoading || ''
    } else {
      this.resultsContainer.removeAttribute('aria-busy')
    }
  }

  handleKeyDown (event) {
    const options = Array.from(this.resultsContainer.querySelectorAll('[role="option"]'))
    const focusableElements = options.map((option) => option.matches('a, button') ? option : option.querySelector('a, button')).filter(Boolean)

    if (event.key === 'Escape' && !this.resultsContainer.hidden) {
      event.stopPropagation()
      this.clearSuggestions()
      this.input.focus()
      return
    }

    if (!focusableElements.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return

    event.preventDefault()
    const direction = event.key === 'ArrowDown' ? 1 : -1
    this.currentIndex = (this.currentIndex + direction + focusableElements.length) % focusableElements.length
    this.updateActiveOption(focusableElements)
  }

  updateActiveOption (focusableElements) {
    focusableElements.forEach((element, index) => {
      const isActive = index === this.currentIndex
      const option = element.closest('[role="option"]')

      option.setAttribute('aria-selected', String(isActive))
      element.tabIndex = isActive ? 0 : -1

      if (isActive) {
        element.focus()
        this.input.setAttribute('aria-activedescendant', option.id)
      }
    })
  }

  debounce (callback, wait) {
    return (...args) => {
      window.clearTimeout(this.debounceTimer)
      this.debounceTimer = window.setTimeout(() => callback(...args), wait)
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch)
